// @ts-check
import { Check, ChevronLeft, ChevronRight, ListChecks, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAcceptAllDiffs, useRejectAllDiffs } from "@/services/chat-provider/canvas"
import { cn } from "@/lib/utils"
import { showConfirm } from "@minohealth/common/utils"

/**
 * Review actions for a document with suggestions: move between them, open the
 * list, and accept or reject all of them.
 * @param {{
 *   documentId: string,
 *   sessionId?: string,
 *   suggestionCount: number,
 *   pendingCount: number,
 *   position: number,
 *   onPrevious: () => void,
 *   onNext: () => void,
 *   listOpen: boolean,
 *   onToggleList: () => void,
 *   listOnlyCount: number,
 *   mockHandlers?: { onAcceptAll: () => void, onRejectAll: () => void },
 * }} props
 */
export default function ReviewBar({
  documentId,
  sessionId,
  suggestionCount,
  pendingCount,
  position,
  onPrevious,
  onNext,
  listOpen,
  onToggleList,
  listOnlyCount,
  mockHandlers,
}) {
  const { mutate: acceptAll, isPending: isAcceptingAll } = useAcceptAllDiffs(documentId, sessionId)
  const { mutate: rejectAll, isPending: isRejectingAll } = useRejectAllDiffs(documentId)
  const isBusy = isAcceptingAll || isRejectingAll

  function handleRejectAll() {
    const what = pendingCount === 1 ? "this suggestion" : `all ${pendingCount} suggestions`
    showConfirm(`Reject ${what}? You cannot get them back.`, "Reject suggestions", "critical")
      .then(() => (mockHandlers ? mockHandlers.onRejectAll() : rejectAll()))
      .catch(() => {})
  }

  return (
    <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 h-11 border-b border-border bg-muted/30 shrink-0">
      <div role="group" aria-label="Move between suggestions" className="flex items-center">
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Previous suggestion" onClick={onPrevious}>
          <ChevronLeft size={15} />
        </Button>
        <span className="min-w-[3.25rem] text-center text-xs tabular-nums text-foreground/85" aria-live="polite">
          {position > 0 ? `${position} of ${suggestionCount}` : `${suggestionCount} ${suggestionCount === 1 ? "edit" : "edits"}`}
        </span>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Next suggestion" onClick={onNext}>
          <ChevronRight size={15} />
        </Button>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className={cn("h-7 gap-1 px-2 text-xs", listOpen && "bg-muted text-foreground")}
        aria-expanded={listOpen}
        title={listOnlyCount > 0 ? `${listOnlyCount} of these can only be reviewed in the list` : undefined}
        onClick={onToggleList}
      >
        <ListChecks size={14} />
        List
        {listOnlyCount > 0 && (
          <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500/20 px-1 text-[10px] font-medium text-amber-700 dark:text-amber-400">
            {listOnlyCount}
          </span>
        )}
      </Button>

      <div data-diff-action className="ml-auto flex items-center gap-1 sm:gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          disabled={pendingCount === 0 || isBusy}
          onClick={handleRejectAll}
        >
          {isRejectingAll ? <Loader2 size={13} className="animate-spin" /> : <X size={13} className="hidden sm:block" />}
          Reject all
        </Button>
        <Button
          size="sm"
          className="h-7 gap-1 px-2 text-xs bg-green-600 text-white hover:bg-green-700"
          disabled={pendingCount === 0 || isBusy}
          onClick={() => (mockHandlers ? mockHandlers.onAcceptAll() : acceptAll())}
        >
          {isAcceptingAll ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} className="hidden sm:block" />}
          Accept all
        </Button>
      </div>
    </div>
  )
}
